import type { EnhancedVerificationRequest } from '@common/types/treefort';
import { Stack } from '@mui/material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSAIDV } from '@/queries/saidv';
import ConfirmWithdrawDialog from './ConfirmWithdrawDialog';
import ReinitiateIDVConfirmDialog from './ReinitiateIDVConfirmDialog';
import ReinitiateIDVModal from './ReinitiateIDVModal';
import ResendIDVModal from './ResendIDVModal';
import StandAloneIDVHeader from './StandAloneIDVHeader';
import StandAloneIDVTable from './StandAloneIDVTable';
import UpdateIDVModal from './UpdateIDVModal';

type ModalKind = 'withdraw' | 'resend' | 'reinitiateConfirm' | 'reinitiate' | 'update';

export default function StandAloneIDVDetails() {
    const { prospectId } = useParams<{ prospectId: string }>();
    const { data: idvDetails } = useSAIDV(prospectId);
    const [kind, setKind] = useState<ModalKind | null>(null);
    const [row, setRow] = useState<EnhancedVerificationRequest | null>(null);

    const prospectVerId = idvDetails?.prospectVersions[0]?.prospectVersionId;
    const quoteApprover = idvDetails?.quoteApprover;

    const openModal = (nextKind: ModalKind, nextRow: EnhancedVerificationRequest) => {
        setKind(nextKind);
        setRow(nextRow);
    };

    const closeModal = () => {
        setKind(null);
        setRow(null);
    };

    return (
        <Stack>
            <StandAloneIDVHeader idvDetails={idvDetails} />
            <StandAloneIDVTable
                prospectId={prospectId}
                prospectVerId={prospectVerId}
                onWithdraw={(nextRow) => openModal('withdraw', nextRow)}
                onResend={(nextRow) => openModal('resend', nextRow)}
                onReinitiate={(nextRow) => openModal('reinitiateConfirm', nextRow)}
                onUpdate={(nextRow) => openModal('update', nextRow)}
            />
            {kind === 'withdraw' && row && (
                <ConfirmWithdrawDialog
                    clientRequestId={row.clientRequestId}
                    prospectId={prospectId}
                    onClose={closeModal}
                />
            )}
            {kind === 'resend' && row && (
                <ResendIDVModal selectedRow={row} prospectId={prospectId} onClose={closeModal} />
            )}
            {kind === 'reinitiateConfirm' && row && (
                <ReinitiateIDVConfirmDialog
                    userName={row.userName}
                    onClose={closeModal}
                    onConfirm={() => setKind('reinitiate')}
                />
            )}
            {kind === 'reinitiate' && row && (
                <ReinitiateIDVModal
                    selectedRow={row}
                    prospectId={prospectId}
                    prospectVerId={prospectVerId}
                    quoteApprover={quoteApprover}
                    onClose={closeModal}
                />
            )}
            {kind === 'update' && row && (
                <UpdateIDVModal
                    selectedRow={row}
                    prospectId={prospectId}
                    prospectVerId={prospectVerId}
                    quoteApprover={quoteApprover}
                    onClose={closeModal}
                />
            )}
        </Stack>
    );
}
